import API from '../../../config/axiosConfig';
import type { RawMedications, RawProtocols } from '../state/wizardData';

const APP_NAME = 'RESUS';
const CONFIG_PROTOCOLS = 'protocols';
const CONFIG_MEDICATIONS = 'medications';

export interface RawResusConfig {
  medicationsRaw: RawMedications;
  protocolsRaw: RawProtocols;
}

export interface UploadResult {
  relativePath: string;
  baseUrl: string;
  sasUrl: string;
}

// The wizard depends on BOTH raw configs (medications cross-references protocol
// IDs), so we fetch them together and only resolve once both succeed. Either
// config may be `{}` if neither the hospital nor the "apps" fallback has it —
// `buildWizardData` tolerates empty inputs.
export async function fetchResusConfig(hospital: string): Promise<RawResusConfig> {
  const [protocolsRes, medicationsRes] = await Promise.all([
    API.get(`/api/configs/${APP_NAME}/${CONFIG_PROTOCOLS}/${hospital}`),
    API.get(`/api/configs/${APP_NAME}/${CONFIG_MEDICATIONS}/${hospital}`)
  ]);
  return {
    protocolsRaw: (protocolsRes.data?.data ?? {}) as RawProtocols,
    medicationsRaw: (medicationsRes.data?.data ?? {}) as RawMedications
  };
}

// Save both raw configs to the target hospital. Run in parallel; if either
// request rejects, the caller surfaces the error and the user retries — we
// have no transactional API and partial-failure recovery would be more
// surprising than asking the user to retry.
export async function saveResusConfig(
  hospital: string,
  configs: RawResusConfig
): Promise<void> {
  await Promise.all([
    API.post(`/api/configs/${APP_NAME}/${CONFIG_PROTOCOLS}/${hospital}`, {
      data: configs.protocolsRaw
    }),
    API.post(`/api/configs/${APP_NAME}/${CONFIG_MEDICATIONS}/${hospital}`, {
      data: configs.medicationsRaw
    })
  ]);
}

export async function uploadResusFile(hospital: string, file: File): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  // handlingErrorLocally: AttachmentList turns the failure into the wizard's
  // own notification; without this the same Hebrew message also appears in the
  // global floating error, twice over.
  const response = await API.post(
    `/api/resus-admin/uploads/${hospital}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, handlingErrorLocally: true }
  );
  return response.data?.data;
}

export async function deleteResusFile(hospital: string, relativePath: string): Promise<void> {
  await API.delete(`/api/resus-admin/uploads/${hospital}`, {
    params: { path: relativePath }
  });
}

export async function getResusFileSasUrl(hospital: string, relativePath: string): Promise<string> {
  const response = await API.get(`/api/resus-admin/uploads/${hospital}/sas`, {
    params: { path: relativePath }
  });
  return response.data?.data?.sasUrl;
}
